import { defineStore } from 'pinia'
import salesOrderService from '@/services/sales-order.service'
import invoiceService from '@/services/invoice.service'
import type { SalesOrder, Invoice, SalesOrderFilters, InvoiceFilters } from '@/types/selling.types'
import type { PaginationMeta } from '@/types/api.types'

export const useSellingStore = defineStore('selling', {
  state: () => ({
    salesOrders: [] as SalesOrder[],
    invoices: [] as Invoice[],
    salesOrdersPagination: null as PaginationMeta | null,
    invoicesPagination: null as PaginationMeta | null,
    loading: false,
    currentSalesOrder: null as SalesOrder | null,
    currentInvoice: null as Invoice | null,
  }),

  actions: {
    // Sales Orders
    async fetchSalesOrders(params?: SalesOrderFilters & { page?: number; page_size?: number }) {
      this.loading = true
      try {
        const { data, meta } = await salesOrderService.getSalesOrders(params)
        this.salesOrders = data
        this.salesOrdersPagination = meta
      } finally {
        this.loading = false
      }
    },

    async fetchSalesOrder(id: string) {
      this.loading = true
      try {
        this.currentSalesOrder = await salesOrderService.getSalesOrder(id)
        return this.currentSalesOrder
      } finally {
        this.loading = false
      }
    },

    // Invoices
    async fetchInvoices(params?: InvoiceFilters & { page?: number; page_size?: number }) {
      this.loading = true
      try {
        const { data, meta } = await invoiceService.getInvoices(params)
        this.invoices = data
        this.invoicesPagination = meta
      } catch (err) {
        this.invoices = []
        this.invoicesPagination = null
        throw err
      } finally {
        this.loading = false
      }
    },

    async fetchInvoice(id: string) {
      this.loading = true
      try {
        this.currentInvoice = await invoiceService.getInvoice(id)
        return this.currentInvoice
      } finally {
        this.loading = false
      }
    },

    async recordPayment(
      invoiceId: string,
      amount: number,
      method?: string,
      notes?: string,
      extras?: {
        treasury_id?: number | null
        treasury_account_id?: string | null
        date?: string
        transaction_id?: string
      },
    ) {
      const payment = await invoiceService.recordPayment({
        invoice_id: invoiceId,
        amount,
        method,
        notes,
        treasury_id: extras?.treasury_id,
        treasury_account_id: extras?.treasury_account_id,
        date: extras?.date,
        transaction_id: extras?.transaction_id,
      })
      // Refresh current invoice if it's the one that was paid
      if (this.currentInvoice && this.currentInvoice.id === invoiceId) {
        await this.fetchInvoice(invoiceId)
      }
      return payment
    },

    async uploadInvoiceAttachment(invoiceId: string, file: File) {
      const attachment = await invoiceService.uploadAttachment(invoiceId, file)
      if (this.currentInvoice?.id === invoiceId) {
        this.currentInvoice.attachments = [attachment, ...(this.currentInvoice.attachments ?? [])]
      }
      return attachment
    },

    async deleteInvoiceAttachment(invoiceId: string, attachmentId: string) {
      await invoiceService.deleteAttachment(invoiceId, attachmentId)
      if (this.currentInvoice?.id === invoiceId) {
        this.currentInvoice.attachments = (this.currentInvoice.attachments ?? []).filter(
          (row) => row.id !== attachmentId,
        )
      }
    },

    async reprintInvoicePdf(invoiceId: string) {
      const invoice = await invoiceService.reprintPdf(invoiceId)
      if (this.currentInvoice?.id === invoiceId) {
        this.currentInvoice = { ...this.currentInvoice, issued_pdf: invoice.issued_pdf ?? null }
      }
      return invoice
    },
  },
})
